import { useState } from 'react'
import { Globe } from '@/components/icons'
import { Button } from '@/components/ui/button'
import { refreshOnlineStatus, useIsOnline } from '@/lib/network'

interface OfflineProviderProps {
  children: React.ReactNode
}

/**
 * Blocking offline gate. The app has no offline mode: while the device is
 * offline a full-screen overlay covers everything (the app stays mounted
 * underneath so the user returns to exactly where they were once the
 * connection is back). Reads the shared onlineManager state (see lib/network).
 */
export function OfflineProvider({ children }: OfflineProviderProps) {
  const isOnline = useIsOnline()

  return (
    <>
      {children}
      {!isOnline && <OfflineScreen />}
    </>
  )
}

function OfflineScreen() {
  const [checking, setChecking] = useState(false)

  return (
    <div
      role="alert"
      className="fixed inset-0 z-9999 flex flex-col items-center justify-center gap-6 bg-surface-L0 px-6 pb-[var(--safe-bottom)] pt-[var(--safe-top)]"
    >
      <div className="flex size-16 items-center justify-center rounded-full bg-surface-L2 text-content-primary-bold">
        <Globe className="size-8" />
      </div>
      <div className="text-center">
        <h1 className="text-lg font-semibold text-content-primary-bold">
          No internet connection
        </h1>
        <p className="mt-1 text-sm text-content-primary-subtle">
          Check your connection and try again.
        </p>
      </div>
      <Button
        variant="primary"
        size="lg"
        loading={checking}
        onClick={async () => {
          setChecking(true)
          try {
            await refreshOnlineStatus()
          } finally {
            setChecking(false)
          }
        }}
      >
        Try Again
      </Button>
    </div>
  )
}
